/**
 * Tela do canal de voz.
 *
 * Duas disposições, escolhidas pelo conteúdo e não por um botão:
 *
 *   - Sem transmissão, uma grade de participantes. As colunas se ajustam à
 *     quantidade de gente, para dois numa chamada não ficarem com dois selos
 *     minúsculos no canto de uma tela vazia.
 *   - Com alguém transmitindo, a tela ocupa o palco e os participantes viram
 *     uma faixa lateral. Quem entra numa chamada com transmissão veio ver a
 *     transmissão.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AudioLines,
  HeadphoneOff,
  Headphones,
  Maximize2,
  Menu,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  Pin,
  ScreenShare,
  Video,
  VideoOff,
  Volume2,
  X,
} from 'lucide-react'
import { ConnectionQuality, type RemoteTrack } from 'livekit-client'
import type { LocalVideoTrack } from 'livekit-client'
import { SCREEN_SHARE_PRESETS, type GuildMember, type ScreenSharePresetId } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useVoiceStore, type FonteDeTransmissao, type RemoteMedia } from '@/store/voice'
import { usePreferencesStore } from '@/store/preferences'
import { useChannelVoiceStates, useMember } from '@/hooks/selectors'
import { Avatar, Button, EmptyState, Tooltip } from '@/components/ui'
import { ScreenSharePicker } from '@/components/voice/ScreenSharePicker'
import { SoundboardTray } from './SoundboardTray'
import { UserContextMenu, type PosicaoDoMenu } from '@/components/layout/UserContextMenu'
import { NicknamePrompt } from '@/components/layout/NicknamePrompt'
import { ScreenAudioMenu } from '@/components/voice/ScreenAudioMenu'

export function VoiceRoom({
  channelId,
  onAbrirMenu,
}: {
  channelId: string
  /** Abre a gaveta de canais. Só existe em tela estreita. */
  onAbrirMenu?: () => void
}) {
  const channel = useAppStore((s) => s.channels[channelId])
  const voiceStates = useChannelVoiceStates(channelId)
  const connectedChannelId = useVoiceStore((s) => s.channelId)
  const connecting = useVoiceStore((s) => s.connecting)
  const connect = useVoiceStore((s) => s.connect)
  const error = useVoiceStore((s) => s.error)
  const micUnavailable = useVoiceStore((s) => s.micUnavailable)

  const remotes = useVoiceStore((s) => s.remotes)
  const localScreenTrack = useVoiceStore((s) => s.localScreenTrack)
  const localCameraTrack = useVoiceStore((s) => s.localCameraTrack)
  const screenSharing = useVoiceStore((s) => s.screenSharing)
  const transmissoesOcultas = useVoiceStore((s) => s.transmissoesOcultas)
  const ocultarTransmissao = useVoiceStore((s) => s.ocultarTransmissao)
  const assistirTransmissao = useVoiceStore((s) => s.assistirTransmissao)

  const myId = useAuthStore((s) => s.user?.id)
  const [pinnedIdentity, setPinnedIdentity] = useState<string | null>(null)

  /** Quem está transmitindo tela agora, incluindo o próprio usuário. */
  const screenSources = useMemo(() => {
    const sources: { identity: string; track: RemoteTrack | LocalVideoTrack; isLocal: boolean }[] = []

    if (screenSharing && localScreenTrack && myId) {
      sources.push({ identity: myId, track: localScreenTrack, isLocal: true })
    }
    for (const [identity, media] of Object.entries(remotes)) {
      if (media.screenTrack) sources.push({ identity, track: media.screenTrack, isLocal: false })
    }
    return sources
  }, [remotes, screenSharing, localScreenTrack, myId])

  /*
    Quem está transmitindo mas foi fechado por esta pessoa.

    Vem do estado que o servidor mantém, e não das faixas recebidas: com a
    inscrição cancelada não chega faixa nenhuma, e sem essa lista a transmissão
    simplesmente desapareceria, sem caminho de volta.
  */
  const fechadas = voiceStates.filter(
    (estado) => estado.screenSharing && transmissoesOcultas.includes(estado.userId),
  )

  const stage = pinnedIdentity
    ? (screenSources.find((s) => s.identity === pinnedIdentity) ?? screenSources[0])
    : screenSources[0]

  if (!channel) return null

  const isConnected = connectedChannelId === channelId

  if (!isConnected) {
    return (
      <div className="relative flex flex-1 flex-col items-center justify-center gap-4 bg-surface-base">
        <button
          type="button"
          onClick={onAbrirMenu}
          aria-label="Abrir canais"
          className="absolute top-2 left-2 flex size-9 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary lg:hidden"
        >
          <Menu className="size-5" />
        </button>

        <EmptyState
          icon={<Volume2 />}
          title={channel.name}
          description={
            voiceStates.length > 0
              ? `${voiceStates.length} ${voiceStates.length === 1 ? 'pessoa está' : 'pessoas estão'} nesta sala.`
              : 'Ninguém está nesta sala no momento.'
          }
          action={
            <Button
              size="lg"
              loading={connecting}
              onClick={() => void connect(channelId, channel.guildId)}
            >
              Entrar no canal
            </Button>
          }
        />

        {/*
          Falha ao entrar precisa aparecer. Sem isto, o botão simplesmente não
          reagia: a conexão caía, a tela continuava igual e não havia nada na
          interface explicando o que houve.
        */}
        {error ? (
          <div className="mx-6 max-w-md rounded-lg bg-danger-subtle p-3 text-center text-[13px] text-danger">
            {error}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-surface-base">
      <header className="flex h-12 shrink-0 items-center gap-3 px-3 shadow-sm sm:px-4">
        <button
          type="button"
          onClick={onAbrirMenu}
          aria-label="Abrir canais"
          className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary lg:hidden"
        >
          <Menu className="size-5" />
        </button>

        <Volume2 className="size-5 shrink-0 text-content-muted" />
        <h1 className="min-w-0 truncate font-semibold text-content-primary">{channel.name}</h1>
        <span className="shrink-0 text-[13px] text-content-muted">
          {voiceStates.length} {voiceStates.length === 1 ? 'participante' : 'participantes'}
        </span>
      </header>

      {/*
        Entrar sem microfone é um estado válido, mas precisa ficar evidente:
        sem este aviso a pessoa fala achando que está sendo ouvida.
      */}
      {micUnavailable ? (
        <div className="mx-3 mt-3 flex items-start gap-2 rounded-lg bg-warning-subtle p-3 text-[13px] text-warning">
          <MicOff className="mt-0.5 size-4 shrink-0" />
          <span>{micUnavailable}</span>
        </div>
      ) : null}

      {/*
        Caminho de volta para o que foi fechado.

        Sem isso, fechar uma transmissão seria uma porta sem maçaneta: ela some
        da tela e não há nada dizendo que ainda está acontecendo.
      */}
      {fechadas.length > 0 ? (
        <div className="mx-3 mt-3 flex flex-wrap items-center gap-2">
          {fechadas.map((estado) => (
            <TransmissaoFechada
              key={estado.userId}
              userId={estado.userId}
              guildId={channel.guildId}
              onAssistir={() => assistirTransmissao(estado.userId)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-3 p-3">
        {stage ? (
          <>
            <div className="min-w-0 flex-1">
              <StageView
                identity={stage.identity}
                track={stage.track}
                isLocal={stage.isLocal}
                guildId={channel.guildId}
                onFechar={
                  stage.isLocal ? undefined : () => ocultarTransmissao(stage.identity)
                }
              />
            </div>

            <div className="flex w-28 shrink-0 flex-col gap-2 overflow-y-auto sm:w-52">
              {screenSources.length > 1
                ? screenSources.map((source) => (
                    <button
                      key={`screen-${source.identity}`}
                      type="button"
                      onClick={() => setPinnedIdentity(source.identity)}
                      className={cn(
                        'relative overflow-hidden rounded-lg bg-surface-deepest transition-all',
                        source.identity === stage.identity
                          ? 'ring-2 ring-brand'
                          : 'opacity-70 hover:opacity-100',
                      )}
                    >
                      <VideoSurface track={source.track} className="aspect-video w-full" muted />
                      <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
                        Tela
                      </span>
                    </button>
                  ))
                : null}

              {voiceStates.map((state) => (
                <ParticipantTile
                  key={state.userId}
                  userId={state.userId}
                  guildId={channel.guildId}
                  compact
                  media={remotes[state.userId]}
                  localCameraTrack={state.userId === myId ? localCameraTrack : null}
                  muted={state.selfMute || state.serverMute}
                  sharing={state.screenSharing}
                />
              ))}
            </div>
          </>
        ) : (
          <ParticipantGrid channelId={channelId} guildId={channel.guildId} />
        )}
      </div>

      <VoiceControlBar channelId={channelId} />
    </div>
  )
}

function ParticipantGrid({ channelId, guildId }: { channelId: string; guildId: string }) {
  const voiceStates = useChannelVoiceStates(channelId)
  const remotes = useVoiceStore((s) => s.remotes)
  const localCameraTrack = useVoiceStore((s) => s.localCameraTrack)
  const myId = useAuthStore((s) => s.user?.id)

  // A grade acompanha a quantidade: poucos participantes ficam grandes, muitos
  // se acomodam sem virar barra de rolagem.
  const columns =
    voiceStates.length <= 1
      ? 'grid-cols-1'
      : voiceStates.length <= 4
        ? 'grid-cols-2'
        : voiceStates.length <= 9
          ? 'grid-cols-3'
          : 'grid-cols-4'

  return (
    <div className={cn('grid flex-1 auto-rows-fr gap-3 overflow-y-auto', columns)}>
      {voiceStates.map((state) => (
        <ParticipantTile
          key={state.userId}
          userId={state.userId}
          guildId={guildId}
          media={remotes[state.userId]}
          localCameraTrack={state.userId === myId ? localCameraTrack : null}
          muted={state.selfMute || state.serverMute}
          sharing={state.screenSharing}
        />
      ))}
    </div>
  )
}

function ParticipantTile({
  userId,
  guildId,
  media,
  localCameraTrack,
  muted,
  sharing,
  compact,
}: {
  userId: string
  guildId: string
  media?: RemoteMedia
  localCameraTrack: LocalVideoTrack | null
  muted: boolean
  sharing: boolean
  compact?: boolean
}) {
  const member = useMember(guildId, userId)
  const myId = useAuthStore((s) => s.user?.id)
  const localSpeaking = useVoiceStore((s) => s.localSpeaking)

  /*
    Espelhar só faz sentido em webcam apontada para o rosto.

    Com uma placa de captura na entrada de vídeo, a imagem invertida atrapalha
    de verdade: quem está jogando vê tudo do lado trocado. E isso sempre valeu
    só para a própria prévia; os outros nunca receberam a imagem espelhada.
  */
  const espelhar = usePreferencesStore((s) => s.video.espelharCamera)

  const [menu, setMenu] = useState<PosicaoDoMenu | null>(null)
  const [apelidoDe, setApelidoDe] = useState<GuildMember | null>(null)

  const isMe = userId === myId
  const speaking = isMe ? localSpeaking : (media?.speaking ?? false)
  const name = member?.nickname ?? member?.user.displayName ?? 'Usuário'
  const cameraTrack = isMe ? localCameraTrack : (media?.cameraTrack ?? null)

  const poorConnection =
    media?.connectionQuality === ConnectionQuality.Poor ||
    media?.connectionQuality === ConnectionQuality.Lost

  return (
    <div
      onContextMenu={(event) => {
        event.preventDefault()
        setMenu({ x: event.clientX, y: event.clientY })
      }}
      className={cn(
        'relative flex items-center justify-center overflow-hidden rounded-xl bg-surface-sunken',
        'transition-shadow duration-150',
        compact ? 'aspect-video' : 'min-h-40',
        speaking && 'ring-2 ring-inset ring-speaking',
      )}
    >
      {menu ? (
        <UserContextMenu
          userId={userId}
          guildId={guildId}
          posicao={menu}
          nomeDeReserva={name}
          onAlterarApelido={setApelidoDe}
          onClose={() => setMenu(null)}
        />
      ) : null}

      <NicknamePrompt membro={apelidoDe} guildId={guildId} onClose={() => setApelidoDe(null)} />

      {cameraTrack ? (
        <VideoSurface
          track={cameraTrack}
          className="size-full object-cover"
          muted={isMe}
          mirror={isMe && espelhar}
        />
      ) : (
        <Avatar
          userId={userId}
          name={name}
          src={member?.user.avatarUrl}
          size={compact ? 'md' : 'xl'}
          speaking={speaking}
        />
      )}

      <div className="absolute inset-x-2 bottom-2 flex items-center gap-1.5">
        <span
          className={cn(
            'min-w-0 truncate rounded bg-black/65 px-1.5 py-0.5 text-white backdrop-blur-sm',
            compact ? 'text-[10px]' : 'text-xs',
          )}
        >
          {name}
          {isMe ? ' (você)' : ''}
        </span>

        {muted ? (
          <span className="rounded bg-black/65 p-1 text-danger backdrop-blur-sm">
            <MicOff className="size-3" />
          </span>
        ) : null}

        {sharing ? (
          <span className="rounded bg-black/65 p-1 text-success backdrop-blur-sm">
            <MonitorUp className="size-3" />
          </span>
        ) : null}

        {poorConnection ? (
          <span className="rounded bg-warning px-1.5 py-0.5 text-[10px] font-medium text-surface-deepest">
            Instável
          </span>
        ) : null}
      </div>
    </div>
  )
}

function StageView({
  identity,
  track,
  isLocal,
  guildId,
  onFechar,
}: {
  identity: string
  track: RemoteTrack | LocalVideoTrack
  isLocal: boolean
  guildId: string
  /** Ausente na própria tela: fechar a si mesmo seria parar de transmitir. */
  onFechar?: () => void
}) {
  const member = useMember(guildId, identity)
  const containerRef = useRef<HTMLDivElement>(null)
  const name = member?.nickname ?? member?.user.displayName ?? 'Usuário'

  const temAudio = useVoiceStore((s) => Boolean(s.remotes[identity]?.screenAudioTrack))
  const [menu, setMenu] = useState<PosicaoDoMenu | null>(null)

  /**
   * Tela cheia, com o desvio que o iPhone exige.
   *
   * No Safari do iOS não existe tela cheia para um elemento qualquer: só o
   * próprio vídeo sabe fazer isso, por um método com nome antigo. Sem este
   * desvio, o botão existia e não fazia nada, que é pior do que não existir.
   */
  const requestFullscreen = () => {
    const container = containerRef.current
    if (!container) return

    if (typeof container.requestFullscreen === 'function') {
      void container.requestFullscreen().catch(() => ampliarPeloVideo(container))
      return
    }

    ampliarPeloVideo(container)
  }

  return (
    <div
      ref={containerRef}
      // O botão direito sobre a transmissão regula o som dela. Na própria tela
      // não faz sentido: ela é exibida muda para quem transmite.
      onContextMenu={
        isLocal
          ? undefined
          : (evento) => {
              evento.preventDefault()
              setMenu({ x: evento.clientX, y: evento.clientY })
            }
      }
      className="group relative flex size-full items-center justify-center overflow-hidden rounded-xl bg-black"
    >
      {menu ? (
        <ScreenAudioMenu
          userId={identity}
          nome={name}
          posicao={menu}
          temAudio={temAudio}
          onFechar={onFechar}
          onClose={() => setMenu(null)}
        />
      ) : null}

      {/* `object-contain` preserva a proporção original: cortar a tela de
          alguém esconde justamente o canto onde costuma estar o que importa. */}
      <VideoSurface track={track} className="size-full object-contain" muted={isLocal} />

      <div className="absolute top-3 left-3 flex items-center gap-2 rounded-lg bg-black/60 px-2.5 py-1.5 backdrop-blur-sm">
        <ScreenShare className="size-4 text-success" />
        <span className="text-[13px] font-medium text-white">
          {isLocal ? 'Sua tela' : `Tela de ${name}`}
        </span>
      </div>

      <div className="revelar-no-hover absolute top-3 right-3 flex gap-1.5">
        <Tooltip content="Tela cheia">
          <button
            type="button"
            onClick={requestFullscreen}
            aria-label="Tela cheia"
            className="rounded-lg bg-black/60 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
          >
            <Maximize2 className="size-4" />
          </button>
        </Tooltip>

        {onFechar ? (
          <Tooltip content="Não assistir esta transmissão">
            <button
              type="button"
              onClick={onFechar}
              aria-label="Não assistir esta transmissão"
              className="rounded-lg bg-black/60 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
            >
              <X className="size-4" />
            </button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

/** Aviso de que alguém continua transmitindo, com o convite para voltar a ver. */
function TransmissaoFechada({
  userId,
  guildId,
  onAssistir,
}: {
  userId: string
  guildId: string
  onAssistir: () => void
}) {
  const member = useMember(guildId, userId)
  const nome = member?.nickname ?? member?.user.displayName ?? 'Alguém'

  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-raised px-3 py-1.5 text-[13px]">
      <ScreenShare className="size-4 shrink-0 text-content-muted" />
      <span className="text-content-secondary">{nome} está transmitindo</span>
      <button
        type="button"
        onClick={onAssistir}
        className="font-medium text-brand transition-colors hover:underline"
      >
        Assistir
      </button>
    </div>
  )
}

/**
 * Caminho de reserva de tela cheia, para quando o navegador só aceita no vídeo.
 *
 * É o caso do iPhone, onde a chamada padrão nem existe. O método tem nome de
 * outra época e não consta na tipagem, então a checagem é feita à mão antes de
 * chamar.
 */
function ampliarPeloVideo(container: HTMLElement): void {
  const video = container.querySelector('video') as
    | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
    | null

  video?.webkitEnterFullscreen?.()
}

/**
 * Liga uma faixa de vídeo a um elemento.
 *
 * `attach`/`detach` do SDK cuidam do ciclo de vida do MediaStream; atribuir
 * `srcObject` na mão vazaria a faixa quando o componente sai da tela.
 */
function VideoSurface({
  track,
  className,
  muted,
  mirror,
}: {
  track: RemoteTrack | LocalVideoTrack
  className?: string
  muted?: boolean
  mirror?: boolean
}) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    track.attach(element)
    return () => {
      track.detach(element)
    }
  }, [track])

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={cn(className, mirror && 'scale-x-[-1]')}
    />
  )
}

function VoiceControlBar({ channelId }: { channelId: string }) {
  const muted = useVoiceStore((s) => s.muted)
  const deafened = useVoiceStore((s) => s.deafened)
  const cameraOn = useVoiceStore((s) => s.cameraOn)
  const screenSharing = useVoiceStore((s) => s.screenSharing)
  const toggleMute = useVoiceStore((s) => s.toggleMute)
  const toggleDeafen = useVoiceStore((s) => s.toggleDeafen)
  const toggleCamera = useVoiceStore((s) => s.toggleCamera)
  const startScreenShare = useVoiceStore((s) => s.startScreenShare)
  const stopScreenShare = useVoiceStore((s) => s.stopScreenShare)
  const disconnect = useVoiceStore((s) => s.disconnect)
  const rnnoiseActive = useVoiceStore((s) => s.rnnoiseActive)

  const defaultPreset = usePreferencesStore((s) => s.video.defaultScreenPreset)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [bandejaAberta, setBandejaAberta] = useState(false)
  const botaoDaBandeja = useRef<HTMLButtonElement>(null)

  const handleShare = (preset: ScreenSharePresetId, fonte: FonteDeTransmissao) => {
    setPickerOpen(false)
    void startScreenShare(preset, fonte)
  }

  void channelId

  return (
    <>
      {/*
        Numa tela estreita o que sai é texto, não botão.

        Somando tudo, a fileira passava de quatrocentos e setenta pixels e não
        cabia em nenhum celular. Os botões continuam com quarenta e quatro
        pixels, que é o mínimo para acertar com o dedo sem errar o vizinho; quem
        cede espaço é a palavra "Sair", que o ícone de telefone já diz, e o selo
        de ruído suprimido, que é aviso e não comando.
      */}
      <div className="flex shrink-0 items-center justify-center gap-1.5 px-2 py-4 sm:gap-2">
        {/*
          Um botão fala do microfone e o outro do fone, e o desenho precisa dizer
          isso sozinho. Os dois mostravam o mesmo alto-falante, o que deixava a
          fila de botões sem sentido: não dava para saber qual deles silenciava a
          própria voz e qual calava a sala inteira.
        */}
        <ControlButton
          active={!muted}
          activeLabel="Silenciar meu microfone"
          inactiveLabel="Ativar meu microfone"
          onClick={() => void toggleMute()}
          danger={muted}
        >
          {muted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
        </ControlButton>

        <ControlButton
          active={!deafened}
          activeLabel="Parar de ouvir a sala"
          inactiveLabel="Voltar a ouvir a sala"
          onClick={toggleDeafen}
          danger={deafened}
        >
          {deafened ? <HeadphoneOff className="size-5" /> : <Headphones className="size-5" />}
        </ControlButton>

        <ControlButton
          active={cameraOn}
          activeLabel="Desligar câmera"
          inactiveLabel="Ligar câmera"
          onClick={() => void toggleCamera()}
        >
          {cameraOn ? <Video className="size-5" /> : <VideoOff className="size-5" />}
        </ControlButton>

        <ControlButton
          active={screenSharing}
          activeLabel="Parar de compartilhar"
          inactiveLabel="Compartilhar tela"
          onClick={() => (screenSharing ? void stopScreenShare() : setPickerOpen(true))}
          onContextMenu={() => setPickerOpen(true)}
        >
          <MonitorUp className="size-5" />
        </ControlButton>

        {/*
          Fica ao lado do compartilhamento porque é o mesmo tipo de gesto: algo
          que você manda para a sala, e não um ajuste da sua própria entrada.
        */}
        <Tooltip content="Efeitos sonoros">
          <button
            ref={botaoDaBandeja}
            type="button"
            onClick={() => setBandejaAberta((v) => !v)}
            aria-label="Efeitos sonoros"
            aria-pressed={bandejaAberta}
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-full transition-colors',
              bandejaAberta
                ? 'bg-brand text-white hover:bg-brand-hover'
                : 'bg-surface-overlay text-content-secondary hover:bg-surface-hover hover:text-content-primary',
            )}
          >
            <AudioLines className="size-5" />
          </button>
        </Tooltip>

        <div className="mx-2 hidden h-8 w-px bg-line sm:block" />

        <Tooltip content="Sair da chamada">
          <button
            type="button"
            onClick={() => void disconnect()}
            aria-label="Sair da chamada"
            className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-danger px-3 text-white transition-colors hover:bg-danger-hover sm:px-5"
          >
            <PhoneOff className="size-5" />
            <span className="hidden text-sm font-medium sm:inline">Sair</span>
          </button>
        </Tooltip>

        {rnnoiseActive ? (
          <Tooltip content="Supressão de ruído por rede neural ativa">
            <span className="ml-2 hidden items-center gap-1.5 rounded-full bg-success-subtle px-2.5 py-1 text-[11px] font-medium text-success sm:flex">
              <Pin className="size-3" />
              Ruído suprimido
            </span>
          </Tooltip>
        ) : null}
      </div>

      {bandejaAberta ? (
        <SoundboardTray ancora={botaoDaBandeja.current} onClose={() => setBandejaAberta(false)} />
      ) : null}

      <ScreenSharePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleShare}
        defaultPreset={defaultPreset}
      />
    </>
  )
}

function ControlButton({
  active,
  activeLabel,
  inactiveLabel,
  danger,
  onClick,
  onContextMenu,
  children,
}: {
  active: boolean
  activeLabel: string
  inactiveLabel: string
  danger?: boolean
  onClick: () => void
  onContextMenu?: () => void
  children: React.ReactNode
}) {
  return (
    <Tooltip content={active ? activeLabel : inactiveLabel}>
      <button
        type="button"
        onClick={onClick}
        onContextMenu={
          onContextMenu
            ? (e) => {
                e.preventDefault()
                onContextMenu()
              }
            : undefined
        }
        aria-label={active ? activeLabel : inactiveLabel}
        aria-pressed={active}
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full transition-colors',
          danger
            ? 'bg-danger text-white hover:bg-danger-hover'
            : active
              ? 'bg-brand text-white hover:bg-brand-hover'
              : 'bg-surface-overlay text-content-secondary hover:bg-surface-hover hover:text-content-primary',
        )}
      >
        {children}
      </button>
    </Tooltip>
  )
}

export { SCREEN_SHARE_PRESETS }
